import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaTrophy,
  FaSpinner,
  FaArrowLeft,
  FaCrown,
  FaMedal,
  FaAward,
  FaUniversity,
  FaUsers,
  FaChartLine,
  FaShieldAlt,
  FaRocket,
  FaStar,
  FaGraduationCap,
  FaCalendarAlt,
  FaBook,
  FaEye,
} from "react-icons/fa";
import { HiOutlineSparkles } from "react-icons/hi";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import toast from "react-hot-toast";
// Import the Footer context
import { useFooter } from "../context/FooterContext";
import { API_BASE_URL } from "../utils/apiConfig";

const Leaderboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setShowFooter } = useFooter(); // Add this line
  const [loading, setLoading] = useState(true);
  const [rankings, setRankings] = useState([]);
  const [universityRankings, setUniversityRankings] = useState([]);
  const [viewingResult, setViewingResult] = useState(false);
  const [viewingUniversityRank, setViewingUniversityRank] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [currentUSN, setCurrentUSN] = useState("");
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Hide footer when component mounts, show when unmounts
  useEffect(() => {
    setShowFooter(false);
    return () => setShowFooter(true);
  }, [setShowFooter]);

  useEffect(() => {
    checkDarkMode();
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const checkMobile = () => {
    setIsMobile(window.innerWidth <= 768);
  };

  const checkDarkMode = () => {
    const dark = document.documentElement.getAttribute("data-theme") === "dark";
    setIsDarkMode(dark);

    const observer = new MutationObserver(() => {
      const dark =
        document.documentElement.getAttribute("data-theme") === "dark";
      setIsDarkMode(dark);
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });
    return () => observer.disconnect();
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const usn = params.get("usn");
    if (usn) {
      setCurrentUSN(usn);
      fetchRankings(usn);
    } else {
      toast.error("USN not provided");
      navigate(-1);
    }
  }, [location.search]);

  const fetchRankings = async (usn) => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/results/leaderboard?usn=${usn}`,
      );
      setRankings(response.data);
    } catch (error) {
      console.error("Fetch rankings error:", error);
      toast.error("Failed to fetch rankings");
    } finally {
      setLoading(false);
    }
  };

  const fetchUniversityRankings = async () => {
    setLoading(true);
    try {
      // Get the year from the current USN (characters 3-5)
      const yearFromUSN = currentUSN.substring(3, 5);
      const response = await axios.get(
        `${API_BASE_URL}/api/results/leaderboard/university?year=${yearFromUSN}&usn=${currentUSN}`,
      );
      setUniversityRankings(response.data);
      setViewingUniversityRank(true);
    } catch (error) {
      console.error("Fetch university rankings error:", error);
      toast.error("Failed to fetch university rankings");
    } finally {
      setLoading(false);
    }
  };

  const viewStudentResult = async (studentUsn, studentName) => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/results/${studentUsn}?examType=Regular`,
      );
      setSelectedStudent({
        ...response.data,
        name: studentName,
        usn: studentUsn,
      });
      setViewingResult(true);
    } catch (error) {
      console.error("Fetch student result error:", error);
      toast.error("Failed to fetch student result");
    } finally {
      setLoading(false);
    }
  };

  const backToRankings = () => {
    setViewingResult(false);
    setSelectedStudent(null);
    setViewingUniversityRank(false);
  };

  const getRankIcon = (rank) => {
    if (rank === 1)
      return <FaCrown style={{ color: "#f59e0b", fontSize: "24px" }} />;
    if (rank === 2)
      return <FaMedal style={{ color: "#94a3b8", fontSize: "22px" }} />;
    if (rank === 3)
      return <FaMedal style={{ color: "#cd7f32", fontSize: "20px" }} />;
    return (
      <span style={{ fontWeight: "bold", fontSize: "16px", color: "#8b5cf6" }}>
        {rank}
      </span>
    );
  };

  const getRankBackground = (rank) => {
    if (rank === 1) return isDarkMode ? "rgba(245,158,11,0.1)" : "#fff9e6";
    if (rank === 2) return isDarkMode ? "rgba(148,163,184,0.1)" : "#f5f5f5";
    if (rank === 3) return isDarkMode ? "rgba(205,127,50,0.1)" : "#fff8f0";
    return "transparent";
  };

  // Helper functions for styling
  const getTextColor = () => (isDarkMode ? "#f1f5f9" : "#1e293b");
  const getTextSecondary = () => (isDarkMode ? "#94a3b8" : "#64748b");
  const getBorderColor = () =>
    isDarkMode ? "rgba(139,92,246,0.2)" : "rgba(139,92,246,0.1)";
  const getCardBackground = () =>
    isDarkMode ? "rgba(30, 30, 45, 0.8)" : "rgba(255, 255, 255, 0.9)";
  const getInputBackground = () => (isDarkMode ? "rgba(0,0,0,0.3)" : "#ffffff");

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { type: "spring", stiffness: 300, damping: 24 },
    },
  };

  // Result display component - WITHOUT PERCENTAGE
  const ResultDisplay = ({ student }) => {
    const groupedSubjects = {};
    student?.subjects?.forEach((subject) => {
      let semester = 0;
      const match = subject.subjectCode.match(/\d{3}/);
      if (match) {
        semester = parseInt(match[0].charAt(0));
      }
      if (!groupedSubjects[semester]) {
        groupedSubjects[semester] = [];
      }
      groupedSubjects[semester].push(subject);
    });

    const sortedSemesters = Object.keys(groupedSubjects).sort((a, b) => b - a);
    const totalMarks =
      student?.subjects?.reduce((sum, s) => sum + s.total, 0) || 0;
    const hasFailed = student?.subjects?.some((s) => s.result === "F");
    const overallResult = hasFailed ? "FAIL" : "PASS";

    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        style={{
          background: getCardBackground(),
          backdropFilter: "blur(20px)",
          borderRadius: "28px",
          overflow: "hidden",
          border: `1px solid ${getBorderColor()}`,
          boxShadow: isDarkMode
            ? "0 25px 50px -12px rgba(0,0,0,0.5)"
            : "0 25px 50px -12px rgba(0,0,0,0.25)",
        }}
      >
        <div
          style={{
            background: "linear-gradient(135deg, #1e1b4b 0%, #2e1065 100%)",
            padding: "20px",
            color: "white",
          }}
        >
          <h2 style={{ marginBottom: "8px" }}>Student Result</h2>
          <p style={{ opacity: 0.9 }}>
            {student?.examMonth || "December"}-{student?.examYear || "2025"}
          </p>
        </div>

        <div style={{ padding: "20px" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "20px",
              flexWrap: "wrap",
              gap: "10px",
            }}
          >
            <div>
              <h1
                style={{
                  fontSize: "24px",
                  color: getTextColor(),
                  marginBottom: "5px",
                }}
              >
                {student?.name}
              </h1>
              <p style={{ color: getTextSecondary() }}>
                <strong>USN:</strong> {student?.usn}
              </p>
              <p style={{ color: getTextSecondary() }}>
                <strong>Semester:</strong> {student?.semester}th Semester
              </p>
            </div>
            <div style={{ textAlign: "center" }}>
              <div
                style={{
                  fontSize: "36px",
                  fontWeight: "bold",
                  color: overallResult === "PASS" ? "#10b981" : "#ef4444",
                }}
              >
                {overallResult}
              </div>
              <div style={{ fontSize: "14px", color: getTextSecondary() }}>
                Overall Result
              </div>
            </div>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)",
              gap: "16px",
              marginBottom: "20px",
              padding: "15px",
              background: isDarkMode
                ? "rgba(139,92,246,0.05)"
                : "rgba(139,92,246,0.03)",
              borderRadius: "16px",
              border: `1px solid ${getBorderColor()}`,
            }}
          >
            <div style={{ textAlign: "center" }}>
              <p style={{ color: getTextSecondary(), fontSize: "12px" }}>
                Total Marks
              </p>
              <p
                style={{
                  fontSize: "24px",
                  fontWeight: "bold",
                  background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                {totalMarks}
              </p>
            </div>
            <div style={{ textAlign: "center" }}>
              <p style={{ color: getTextSecondary(), fontSize: "12px" }}>
                Class Rank
              </p>
              <p
                style={{
                  fontSize: "24px",
                  fontWeight: "bold",
                  background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                {student?.classRank || "N/A"}
              </p>
            </div>
          </div>

          <h3
            style={{
              fontSize: "18px",
              fontWeight: "bold",
              marginBottom: "16px",
              color: getTextColor(),
            }}
          >
            Subject-wise Performance
          </h3>

          {sortedSemesters.map((semester) => (
            <div key={semester} style={{ marginBottom: "24px" }}>
              <h4
                style={{
                  background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
                  color: "white",
                  padding: "8px 12px",
                  borderRadius: "10px",
                  marginBottom: "12px",
                  fontSize: "14px",
                }}
              >
                Semester {semester} Subjects
              </h4>
              <div style={{ overflowX: "auto" }}>
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    fontSize: "13px",
                  }}
                >
                  <thead>
                    <tr
                      style={{
                        background: isDarkMode
                          ? "rgba(139,92,246,0.05)"
                          : "#f8f9fa",
                        borderBottom: `2px solid ${getBorderColor()}`,
                      }}
                    >
                      <th
                        style={{
                          padding: "10px",
                          textAlign: "left",
                          color: getTextColor(),
                        }}
                      >
                        Subject Code
                      </th>
                      <th
                        style={{
                          padding: "10px",
                          textAlign: "left",
                          color: getTextColor(),
                        }}
                      >
                        Subject Name
                      </th>
                      <th
                        style={{
                          padding: "10px",
                          textAlign: "center",
                          color: getTextColor(),
                        }}
                      >
                        Internal
                      </th>
                      <th
                        style={{
                          padding: "10px",
                          textAlign: "center",
                          color: getTextColor(),
                        }}
                      >
                        External
                      </th>
                      <th
                        style={{
                          padding: "10px",
                          textAlign: "center",
                          color: getTextColor(),
                        }}
                      >
                        Total
                      </th>
                      <th
                        style={{
                          padding: "10px",
                          textAlign: "center",
                          color: getTextColor(),
                        }}
                      >
                        Result
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {groupedSubjects[semester].map((subject, idx) => (
                      <tr
                        key={idx}
                        style={{
                          borderBottom: `1px solid ${getBorderColor()}`,
                        }}
                      >
                        <td
                          style={{
                            padding: "10px",
                            fontFamily: "monospace",
                            color: "#8b5cf6",
                          }}
                        >
                          {subject.subjectCode}
                        </td>
                        <td style={{ padding: "10px", color: getTextColor() }}>
                          {subject.subjectName}
                        </td>
                        <td
                          style={{
                            padding: "10px",
                            textAlign: "center",
                            color: getTextColor(),
                          }}
                        >
                          {subject.internal}
                        </td>
                        <td
                          style={{
                            padding: "10px",
                            textAlign: "center",
                            color: getTextColor(),
                          }}
                        >
                          {subject.external}
                        </td>
                        <td
                          style={{
                            padding: "10px",
                            textAlign: "center",
                            fontWeight: "bold",
                            color: getTextColor(),
                          }}
                        >
                          {subject.total}
                        </td>
                        <td
                          style={{
                            padding: "10px",
                            textAlign: "center",
                            fontWeight: "bold",
                            color:
                              subject.result === "P" ? "#10b981" : "#ef4444",
                          }}
                        >
                          {subject.result}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr
                      style={{
                        background: isDarkMode
                          ? "rgba(139,92,246,0.05)"
                          : "#f8f9fa",
                      }}
                    >
                      <td
                        colSpan="5"
                        style={{
                          padding: "10px",
                          textAlign: "right",
                          fontWeight: "bold",
                          color: getTextColor(),
                        }}
                      >
                        Semester Total:
                      </td>
                      <td
                        style={{
                          padding: "10px",
                          textAlign: "center",
                          fontWeight: "bold",
                          color: "#8b5cf6",
                        }}
                      >
                        {groupedSubjects[semester].reduce(
                          (sum, s) => sum + s.total,
                          0,
                        )}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          ))}

          <div
            style={{
              display: "flex",
              gap: "12px",
              marginTop: "20px",
            }}
          >
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={backToRankings}
              style={{
                flex: 1,
                padding: "10px",
                background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
                color: "white",
                border: "none",
                borderRadius: "12px",
                cursor: "pointer",
                fontWeight: "500",
              }}
            >
              Back to Rankings
            </motion.button>
          </div>
        </div>
      </motion.div>
    );
  };

  if (viewingResult && selectedStudent) {
    return (
      <div
        style={{
          minHeight: "calc(100vh - 200px)",
          background: isDarkMode ? "#0a0a0f" : "#f5f7fb",
          padding: "40px 20px",
          position: "relative",
        }}
      >
        <div
          style={{
            maxWidth: "1000px",
            margin: "0 auto",
            position: "relative",
            zIndex: 1,
          }}
        >
          <ResultDisplay student={selectedStudent} />
        </div>
      </div>
    );
  }

  if (viewingUniversityRank) {
    return (
      <div
        style={{
          minHeight: "calc(100vh - 200px)",
          background: isDarkMode ? "#0a0a0f" : "#f5f7fb",
          padding: isMobile ? "20px 16px" : "40px 20px",
          position: "relative",
        }}
      >
        {/* Animated Background */}
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            pointerEvents: "none",
            zIndex: 0,
            backgroundImage: `radial-gradient(circle at 1px 1px, ${isDarkMode ? "rgba(139,92,246,0.1)" : "rgba(139,92,246,0.08)"} 1px, transparent 1px)`,
            backgroundSize: "40px 40px",
          }}
        />

        <div
          style={{
            maxWidth: "1200px",
            margin: "0 auto",
            position: "relative",
            zIndex: 1,
          }}
        >
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            style={{
              background: getCardBackground(),
              backdropFilter: "blur(20px)",
              borderRadius: "28px",
              overflow: "hidden",
              border: `1px solid ${getBorderColor()}`,
              boxShadow: isDarkMode
                ? "0 25px 50px -12px rgba(0,0,0,0.5)"
                : "0 25px 50px -12px rgba(0,0,0,0.25)",
            }}
          >
            {/* NEW HEADER - Paste the code here */}
            <div
              style={{
                padding: "20px",
                background: "linear-gradient(135deg, #1e1b4b 0%, #2e1065 100%)",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: "10px",
              }}
            >
              <div>
                <div
                  style={{ display: "flex", alignItems: "center", gap: "10px" }}
                >
                  <div
                    style={{
                      background: "rgba(255,255,255,0.2)",
                      width: "40px",
                      height: "40px",
                      borderRadius: "12px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <FaTrophy size={20} color="#f59e0b" />
                  </div>
                  <h2 style={{ color: "white", marginBottom: "5px" }}>
                    University Rankings{" "}
                    {currentUSN && `- Year ${currentUSN.substring(3, 5)}`}
                  </h2>
                </div>
                <p
                  style={{
                    opacity: 0.8,
                    fontSize: "13px",
                    color: "rgba(255,255,255,0.8)",
                  }}
                >
                  Top performers across all colleges and branches for your batch
                </p>
              </div>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={backToRankings}
                style={{
                  padding: "8px 16px",
                  background: "rgba(255,255,255,0.15)",
                  border: "none",
                  borderRadius: "10px",
                  color: "white",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  backdropFilter: "blur(10px)",
                }}
              >
                <FaArrowLeft /> Back to Class Rankings
              </motion.button>
            </div>

            {/* University Rankings Table */}
            <div style={{ padding: "20px", overflowX: "auto" }}>
              {loading ? (
                <div style={{ textAlign: "center", padding: "60px" }}>
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{
                      duration: 1,
                      repeat: Infinity,
                      ease: "linear",
                    }}
                  >
                    <FaSpinner size={30} color="#8b5cf6" />
                  </motion.div>
                </div>
              ) : universityRankings.length === 0 ? (
                <div style={{ textAlign: "center", padding: "60px" }}>
                  <FaTrophy size={40} color={getTextSecondary()} />
                  <h3 style={{ marginTop: "12px", color: getTextColor() }}>
                    No Rankings Found
                  </h3>
                  <p style={{ color: getTextSecondary(), fontSize: "13px" }}>
                    No other students found from your batch year.
                  </p>
                </div>
              ) : (
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr
                      style={{
                        background: isDarkMode
                          ? "rgba(139,92,246,0.05)"
                          : "#f8f9fa",
                        borderBottom: `2px solid ${getBorderColor()}`,
                      }}
                    >
                      <th
                        style={{
                          padding: "12px",
                          textAlign: "center",
                          width: "80px",
                          color: getTextColor(),
                        }}
                      >
                        Rank
                      </th>
                      <th
                        style={{
                          padding: "12px",
                          textAlign: "left",
                          color: getTextColor(),
                        }}
                      >
                        Student Name
                      </th>
                      <th
                        style={{
                          padding: "12px",
                          textAlign: "left",
                          color: getTextColor(),
                        }}
                      >
                        USN
                      </th>
                      <th
                        style={{
                          padding: "12px",
                          textAlign: "left",
                          color: getTextColor(),
                        }}
                      >
                        College
                      </th>
                      <th
                        style={{
                          padding: "12px",
                          textAlign: "left",
                          color: getTextColor(),
                        }}
                      >
                        Branch
                      </th>
                      <th
                        style={{
                          padding: "12px",
                          textAlign: "center",
                          color: getTextColor(),
                        }}
                      >
                        Year
                      </th>
                      <th
                        style={{
                          padding: "12px",
                          textAlign: "center",
                          color: getTextColor(),
                        }}
                      >
                        Total Marks
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {universityRankings.map((student) => (
                      <tr
                        key={student.usn}
                        style={{
                          borderBottom: `1px solid ${getBorderColor()}`,
                          background: getRankBackground(student.rank),
                          cursor: "pointer",
                        }}
                        onClick={() =>
                          viewStudentResult(student.usn, student.name)
                        }
                      >
                        <td style={{ padding: "12px", textAlign: "center" }}>
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                            }}
                          >
                            {getRankIcon(student.rank)}
                          </div>
                        </td>
                        <td
                          style={{
                            padding: "12px",
                            fontWeight: "500",
                            color: getTextColor(),
                          }}
                        >
                          {student.name}
                        </td>
                        <td
                          style={{
                            padding: "12px",
                            fontFamily: "monospace",
                            color: "#8b5cf6",
                          }}
                        >
                          {student.usn}
                        </td>
                        <td style={{ padding: "12px", color: getTextColor() }}>
                          {student.collegeCode || "N/A"}
                        </td>
                        <td style={{ padding: "12px", color: getTextColor() }}>
                          {student.branchCode || "N/A"}
                        </td>
                        <td
                          style={{
                            padding: "12px",
                            textAlign: "center",
                            color: getTextColor(),
                          }}
                        >
                          {student.year || "N/A"}
                        </td>
                        <td
                          style={{
                            padding: "12px",
                            textAlign: "center",
                            fontWeight: "bold",
                            color: "#8b5cf6",
                          }}
                        >
                          {student.totalMarks}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        minHeight: "calc(100vh - 200px)",
        background: isDarkMode ? "#0a0a0f" : "#f5f7fb",
        padding: isMobile ? "20px 16px" : "40px 20px",
        position: "relative",
      }}
    >
      {/* Animated Background Grid */}
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          pointerEvents: "none",
          zIndex: 0,
          backgroundImage: `radial-gradient(circle at 1px 1px, ${isDarkMode ? "rgba(139,92,246,0.1)" : "rgba(139,92,246,0.08)"} 1px, transparent 1px)`,
          backgroundSize: "40px 40px",
        }}
      />

      {/* Floating Gradient Orbs */}
      <motion.div
        animate={{ x: [0, 100, 0], y: [0, 50, 0] }}
        transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
        style={{
          position: "fixed",
          top: "10%",
          left: "-5%",
          width: "250px",
          height: "250px",
          background:
            "radial-gradient(circle, rgba(139,92,246,0.3) 0%, transparent 70%)",
          borderRadius: "50%",
          filter: "blur(60px)",
          pointerEvents: "none",
          zIndex: 0,
        }}
      />

      <div
        style={{
          maxWidth: "1000px",
          margin: "0 auto",
          position: "relative",
          zIndex: 1,
        }}
      >
        {/* Back Button */}
        <motion.button
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          whileHover={{ scale: 1.02, x: -5 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => navigate(-1)}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            background: isDarkMode
              ? "rgba(139,92,246,0.1)"
              : "rgba(139,92,246,0.05)",
            border: `1px solid ${getBorderColor()}`,
            color: "#8b5cf6",
            cursor: "pointer",
            marginBottom: "20px",
            fontSize: "14px",
            padding: "8px 16px",
            borderRadius: "10px",
            transition: "all 0.3s",
          }}
        >
          <FaArrowLeft /> Back to Result
        </motion.button>

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, type: "spring", stiffness: 300 }}
          style={{ textAlign: "center", marginBottom: "30px" }}
        >
          <motion.div
            animate={{ rotate: [0, 10, -10, 0] }}
            transition={{ duration: 3, repeat: Infinity, repeatDelay: 2 }}
            style={{ display: "inline-block", marginBottom: "1rem" }}
          >
            <div
              style={{
                background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
                width: "60px",
                height: "60px",
                borderRadius: "20px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto",
              }}
            >
              <FaTrophy size={28} color="white" />
            </div>
          </motion.div>
          <h1
            style={{
              fontSize: isMobile ? "28px" : "32px",
              fontWeight: "bold",
              background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              marginBottom: "8px",
            }}
          >
            Class Rankings
          </h1>
          <p style={{ color: getTextSecondary(), fontSize: "14px" }}>
            Students from your college ({currentUSN.substring(1, 3)}), year (
            {currentUSN.substring(3, 5)}), branch ({currentUSN.substring(5, 7)})
          </p>
        </motion.div>

        {/* University Rankings Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          style={{
            display: "flex",
            justifyContent: "flex-end",
            marginBottom: "20px",
          }}
        >
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={fetchUniversityRankings}
            style={{
              padding: "10px 20px",
              background: "linear-gradient(135deg, #1e1b4b, #2e1065)",
              color: "white",
              border: "none",
              borderRadius: "12px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              fontWeight: "500",
              boxShadow: "0 4px 14px 0 rgba(139,92,246,0.3)",
            }}
          >
            <FaUniversity /> View University Rankings
          </motion.button>
        </motion.div>

        {/* Rankings Table */}
        {loading ? (
          <div style={{ textAlign: "center", padding: "60px" }}>
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
            >
              <FaSpinner size={30} color="#8b5cf6" />
            </motion.div>
          </div>
        ) : rankings.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            style={{
              textAlign: "center",
              padding: "60px",
              background: getCardBackground(),
              backdropFilter: "blur(20px)",
              borderRadius: "24px",
              border: `1px solid ${getBorderColor()}`,
            }}
          >
            <FaTrophy size={40} color={getTextSecondary()} />
            <h3
              style={{
                marginTop: "12px",
                color: getTextColor(),
                fontSize: "16px",
              }}
            >
              No Rankings Found
            </h3>
            <p style={{ color: getTextSecondary(), fontSize: "13px" }}>
              No other students found from your college, year, and branch.
            </p>
          </motion.div>
        ) : (
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            style={{
              background: getCardBackground(),
              backdropFilter: "blur(20px)",
              borderRadius: "24px",
              overflow: "hidden",
              border: `1px solid ${getBorderColor()}`,
              boxShadow: isDarkMode
                ? "0 25px 50px -12px rgba(0,0,0,0.5)"
                : "0 25px 50px -12px rgba(0,0,0,0.25)",
            }}
          >
            <div style={{ overflowX: "auto" }}>
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: "14px",
                }}
              >
                <thead>
                  <tr
                    style={{
                      background: isDarkMode
                        ? "rgba(139,92,246,0.05)"
                        : "#f8f9fa",
                      borderBottom: `2px solid ${getBorderColor()}`,
                    }}
                  >
                    <th
                      style={{
                        padding: "12px",
                        textAlign: "center",
                        width: "80px",
                        color: getTextColor(),
                      }}
                    >
                      Rank
                    </th>
                    <th
                      style={{
                        padding: "12px",
                        textAlign: "left",
                        color: getTextColor(),
                      }}
                    >
                      Student Name
                    </th>
                    <th
                      style={{
                        padding: "12px",
                        textAlign: "left",
                        color: getTextColor(),
                      }}
                    >
                      USN
                    </th>
                    <th
                      style={{
                        padding: "12px",
                        textAlign: "center",
                        color: getTextColor(),
                      }}
                    >
                      Total Marks
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rankings.map((student, idx) => (
                    <motion.tr
                      key={student.usn}
                      variants={itemVariants}
                      style={{
                        borderBottom: `1px solid ${getBorderColor()}`,
                        background: getRankBackground(student.rank),
                        cursor: "pointer",
                      }}
                      onClick={() =>
                        viewStudentResult(student.usn, student.name)
                      }
                    >
                      <td style={{ padding: "12px", textAlign: "center" }}>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                          }}
                        >
                          {getRankIcon(student.rank)}
                        </div>
                      </td>
                      <td
                        style={{
                          padding: "12px",
                          fontWeight: "500",
                          color: getTextColor(),
                        }}
                      >
                        {student.name || "Student"}
                      </td>
                      <td
                        style={{
                          padding: "12px",
                          fontFamily: "monospace",
                          fontSize: "12px",
                          color: "#8b5cf6",
                        }}
                      >
                        {student.usn || "N/A"}
                      </td>
                      <td
                        style={{
                          padding: "12px",
                          textAlign: "center",
                          fontWeight: "bold",
                          color: "#8b5cf6",
                        }}
                      >
                        {student.totalMarks || 0}
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          </motion.div>
        )}

        <style>
          {`
            @keyframes spin {
              from { transform: rotate(0deg); }
              to { transform: rotate(360deg); }
            }
          `}
        </style>
      </div>
    </div>
  );
};

export default Leaderboard;
